import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import LoaderBox from "./LoaderBox";
import { useLocation } from "react-router-dom";

const getDayRank = (dayStr) => {
  if (!dayStr) return 99;
  const upper = dayStr.toUpperCase();

  if (upper.includes("PON")) return 1;
  if (upper.includes("UTO")) return 2;
  if (upper.includes("SRI")) return 3;
  if (upper.includes("ČET") || upper.includes("CET")) return 4;
  if (upper.includes("PET")) return 5;
  if (upper.includes("SUB")) return 6;
  if (upper.includes("NED")) return 7;

  return 50;
};

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const cleanTime = timeStr.trim();
  const parts = cleanTime.split(":");
  if (parts.length >= 2) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours * 60 + minutes;
  }
  return 0;
};

const sortMatches = (a, b) => {
  const rankA = getDayRank(a.match_day);
  const rankB = getDayRank(b.match_day);

  // 1. Prvo strogo gleda dane (PON -> NED)
  if (rankA !== rankB) {
    return rankA - rankB;
  }

  // 2. Unutar istog dana gleda vrijeme
  const minutesA = parseTimeToMinutes(a.match_time);
  const minutesB = parseTimeToMinutes(b.match_time);
  return minutesA - minutesB;
};

const HomeResults = ({ turnirId }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMatch, setExpandedMatch] = useState(null);
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState("Svi rezultati");
  const [filterList, setFilterList] = useState(["Svi rezultati"]);
  const [hideDataMode, setHideDataMode] = useState(false);

  const filteredResults = hideDataMode
    ? []
    : results
        .filter((m) => {
          const matchesFilter =
            activeFilter === "Svi rezultati" || m.group_name === activeFilter;
          const matchesSearch = searchQuery
            ? m.team_a?.name.toLowerCase().includes(searchQuery) ||
              m.team_b?.name.toLowerCase().includes(searchQuery)
            : true;
          return matchesFilter && matchesSearch;
        })
        .sort(sortMatches);

  useEffect(() => {
    const fetchResults = async () => {
      if (!turnirId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const { data: settings, error: settingsError } = await supabase
        .from("admin_turniri")
        .select("prikazi_rezultate, hide_data_mode")
        .eq("id", turnirId)
        .single();

      if (settingsError) {
        console.error(
          "❌ Greška pri dohvaćanju admin_turniri:",
          settingsError.message,
        );
      }

      if (settings) {
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        setHideDataMode(
          isLocalhost
            ? false
            : !settings.prikazi_rezultate || settings.hide_data_mode,
        );
      }

      // Dohvat regularnih rezultata
      const { data: regData, error: regError } = await supabase
        .from("turniri_utakmice")
        .select(
          `
          *,
          team_a:team_a_id(name, logo_url),
          team_b:team_b_id(name, logo_url)
        `,
        )
        .eq("tournament_id", turnirId)
        .eq("is_completed", true);

      if (regError) {
        console.error(
          "❌ Greška pri dohvaćanju regularnih rezultata:",
          regError.message,
        );
      }

      // Dohvat veteranskih rezultata
      const { data: vetData, error: vetError } = await supabase
        .from("turniri_veterani")
        .select(
          `
          *,
          team_a:team_a_id(name, logo_url),
          team_b:team_b_id(name, logo_url)
        `,
        )
        .eq("tournament_id", turnirId)
        .eq("is_completed", true);

      if (vetError) {
        console.error(
          "❌ Greška pri dohvaćanju veteranskih rezultata:",
          vetError.message,
        );
      }

      const formattedReg = (regData || []).map((m) => ({
        ...m,
        isVeteran: false,
      }));
      const formattedVet = (vetData || []).map((m) => ({
        ...m,
        isVeteran: true,
      }));

      const combinedData = [...formattedReg, ...formattedVet];

      if (combinedData.length > 0) {
        combinedData.sort(sortMatches);
        setResults(combinedData);

        const groups = combinedData.map((m) => m.group_name).filter(Boolean);
        const uniqueGroups = [...new Set(groups)];

        uniqueGroups.sort((a, b) => {
          if (a === "Završnica") return 1;
          if (b === "Završnica") return -1;
          return a.localeCompare(b);
        });

        setFilterList(["Svi rezultati", ...uniqueGroups]);
      } else {
        setResults([]);
        setFilterList(["Svi rezultati"]);
      }
      setLoading(false);
    };

    fetchResults();
  }, [turnirId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get("search");
    if (search) setSearchQuery(decodeURIComponent(search).toLowerCase());
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightTeam = params.get("highlight");

    if (highlightTeam) {
      const match = results.find(
        (m) =>
          m.team_a?.name.toLowerCase() === highlightTeam.toLowerCase() ||
          m.team_b?.name.toLowerCase() === highlightTeam.toLowerCase(),
      );

      if (match) {
        if (activeFilter !== match.group_name) {
          setActiveFilter(match.group_name);
          return;
        }

        const element = document.getElementById(`match-${match.id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("highlight-pulse");

          setTimeout(() => {
            element.classList.remove("highlight-pulse");
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          }, 3000);
        }
      }
    }
  }, [location.search, activeFilter, results]);

  const toggleDetails = (matchId) => {
    setExpandedMatch(expandedMatch === matchId ? null : matchId);
  };

  if (loading) return <LoaderBox text="Učitavanje rezultata..." />;

  const EventIcon = ({ type }) => {
    switch (type) {
      case "goal":
        return (
          <div className="goal-icon" title="Gol">
            <i className="fi fi-br-football"></i>
          </div>
        );
      case "penalty":
        return (
          <div className="penalty-icon" title="Penal">
            <i className="fi fi-br-football"></i>
          </div>
        );
      case "penalty-miss":
        return (
          <div className="penalty-icon miss" title="Promašaj">
            <i className="fi fi-rs-cross-small"></i>
          </div>
        );
      case "yellow":
        return (
          <div className="two-min-out-icon" title="2 min">
            <p>2</p>
            <span>min</span>
          </div>
        );
      case "og":
        return (
          <div className="autogoal c-og" title="Autogol">
            AG
          </div>
        );
      default:
        return <div className="c-icon c-default">?</div>;
    }
  };

  return (
    <div className="results-view-wrapper">
      <div className="day-navigation-tabs results-filter-tabs">
        {filterList.map((f) => (
          <button
            key={f}
            className={activeFilter === f ? "active" : ""}
            onClick={() => {
              setActiveFilter(f);
              setExpandedMatch(null);
            }}
          >
            {f === "Svi rezultati"
              ? "Svi rezultati"
              : f === "Završnica"
                ? "Završnica"
                : `Grupa ${f}`}
          </button>
        ))}
      </div>

      <div className="results-container">
        {filteredResults.length === 0 ? (
          <div className="empty-state results-empty">
            <div className="icon-wrapper">
              <i className="fi fi-rs-info"></i>
            </div>
            <p>Nema rezultata za odabrani filter.</p>
          </div>
        ) : (
          filteredResults.map((match) => (
            <div
              key={match.id}
              id={`match-${match.id}`}
              className={`result-card ${expandedMatch === match.id ? "active" : ""}`}
              style={{ position: "relative" }}
            >
              {match.isVeteran && (
                <span className="veteran-corner-badge">VETERANI</span>
              )}

              <div className="result-group-tag">
                <span>
                  {match.group_name === "Završnica"
                    ? "Završnica"
                    : `Grupa ${match.group_name}`}
                </span>
                <span>
                  {match.match_time
                    ? `${match.match_day} u ${match.match_time}`
                    : match.match_day}
                </span>
              </div>

              <div
                className="result-main"
                onClick={() => toggleDetails(match.id)}
              >
                <div className="res-team left">
                  <img src={match.team_a?.logo_url || ShieldImg} alt="logo" />
                  <span className="res-team-name">{match.team_a?.name}</span>
                </div>

                <div className="res-score">
                  <span className="score-num">{match.scorea}</span>
                  <span className="score-divider">:</span>
                  <span className="score-num">{match.scoreb}</span>
                </div>

                <div className="res-team right">
                  <img src={match.team_b?.logo_url || ShieldImg} alt="logo" />
                  <span className="res-team-name">{match.team_b?.name}</span>
                </div>

                <div
                  className={`res-arrow ${expandedMatch === match.id ? "open" : ""}`}
                >
                  <i className="fi fi-rs-angle-small-down"></i>
                </div>
              </div>

              {expandedMatch === match.id && (
                <div className="result-details">
                  <div className="details-header">Tijek utakmice</div>
                  <div className="details-timeline">
                    {match.events && match.events.length > 0 ? (
                      match.events
                        .slice()
                        .sort((a, b) => parseInt(a.time) - parseInt(b.time))
                        .map((event, idx) => (
                          <div
                            key={idx}
                            className={`detail-row ${event.team === "A" ? "left" : "right"}`}
                          >
                            <div className="detail-info">
                              <span className="detail-time">{event.time}'</span>
                              <EventIcon type={event.type} />
                              <span className="detail-player">
                                {event.player}
                              </span>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="no-events">
                        Nema zabilježenih događaja.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HomeResults;
